import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Star, Check, X, Trash2, Eye, EyeOff, LogOut, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarDisplay({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating ? "text-orange-500 fill-orange-500" : "text-gray-400"
          }`}
        />
      ))}
    </div>
  );
}

export default function AdminReviews() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [showEmails, setShowEmails] = useState({});
  
  const getAuthHeader = () => {
    return "Basic " + btoa(`${username}:${password}`);
  };
  
  const fetchReviews = async () => {
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews`, {
        headers: {
          "Authorization": getAuthHeader()
        }
      });
      
      if (response.status === 401) {
        setIsAuthenticated(false);
        setError("Authentication failed");
        return;
      }
      
      const data = await response.json();
      setReviews(data);
      setIsAuthenticated(true);
    } catch (err) {
      setError("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    await fetchReviews();
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername("");
    setPassword("");
    setReviews([]);
  };
  
  const handleApprove = async (reviewId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}/approve`, {
        method: "PUT",
        headers: {
          "Authorization": getAuthHeader()
        }
      });
      
      if (response.ok) {
        await fetchReviews();
      }
    } catch (err) {
      setError("Failed to approve review");
    }
  };
  
  const handleReject = async (reviewId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}/reject`, {
        method: "PUT",
        headers: {
          "Authorization": getAuthHeader()
        }
      });
      
      if (response.ok) {
        await fetchReviews();
      }
    } catch (err) {
      setError("Failed to reject review");
    }
  };
  
  const handleDelete = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review?")) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: "DELETE",
        headers: {
          "Authorization": getAuthHeader()
        }
      });
      
      if (response.ok) {
        await fetchReviews();
      }
    } catch (err) {
      setError("Failed to delete review");
    }
  };
  
  const toggleEmail = (reviewId) => {
    setShowEmails(prev => ({
      ...prev,
      [reviewId]: !prev[reviewId]
    }));
  };
  
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('de-DE');
    } catch {
      return dateString;
    }
  };
  
  const filteredReviews = reviews.filter(review => {
    if (filter === "all") return true;
    return review.status === filter;
  });
  
  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "text-green-400 bg-green-400/10";
      case "rejected": return "text-red-400 bg-red-400/10";
      default: return "text-yellow-400 bg-yellow-400/10";
    }
  };
  
  const getPhotoPermissionLabel = (permission) => {
    switch (permission) {
      case "yes": return "Ja";
      case "no": return "Nein";
      case "anonymous": return "Nur anonym";
      default: return permission;
    }
  };
  
  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#1e2a3a] pt-24 pb-16 px-4" data-testid="admin-login">
        <div className="max-w-md mx-auto">
          <h1 className="text-2xl font-bold text-white text-center mb-8">
            Admin Login
          </h1>
          
          <Card className="bg-[#2d3a4a] border-0">
            <CardContent className="p-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-white text-sm mb-2">Username</label>
                  <Input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="bg-[#1e2a3a] border-gray-600 text-white"
                    required
                    data-testid="admin-username"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm mb-2">Password</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-[#1e2a3a] border-gray-600 text-white"
                    required
                    data-testid="admin-password"
                  />
                </div>
                
                {error && (
                  <div className="text-red-400 text-sm">{error}</div>
                )}
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  data-testid="admin-login-btn"
                >
                  {loading ? "Loading..." : "Login"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#1e2a3a] pt-24 pb-16 px-4" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-white">
            Review Management
          </h1>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchReviews}
              variant="outline"
              className="border-gray-600 text-white hover:bg-gray-700"
              data-testid="admin-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-gray-600 text-white hover:bg-gray-700"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#2d3a4a] border-0">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{reviews.length}</div>
              <div className="text-gray-400 text-sm">Total</div>
            </CardContent>
          </Card>
          <Card className="bg-[#2d3a4a] border-0">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-400">
                {reviews.filter(r => r.status === "pending").length}
              </div>
              <div className="text-gray-400 text-sm">Pending</div>
            </CardContent>
          </Card>
          <Card className="bg-[#2d3a4a] border-0">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-400">
                {reviews.filter(r => r.status === "approved").length}
              </div>
              <div className="text-gray-400 text-sm">Approved</div>
            </CardContent>
          </Card>
          <Card className="bg-[#2d3a4a] border-0">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-400">
                {reviews.filter(r => r.status === "rejected").length}
              </div>
              <div className="text-gray-400 text-sm">Rejected</div>
            </CardContent>
          </Card>
        </div>
        
        {/* Filter */}
        <div className="flex gap-2 mb-6">
          {["all", "pending", "approved", "rejected"].map(f => (
            <Button
              key={f}
              onClick={() => setFilter(f)}
              className={`capitalize ${
                filter === f
                  ? "bg-orange-500 hover:bg-orange-600"
                  : "bg-gray-600 hover:bg-gray-700"
              }`}
            >
              {f}
            </Button>
          ))}
        </div>
        
        {/* Reviews List */}
        <div className="space-y-4">
          {filteredReviews.length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              No reviews found
            </div>
          ) : (
            filteredReviews.map(review => (
              <Card key={review.id} className="bg-[#2d3a4a] border-0">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    {/* Review Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-white font-semibold text-lg">
                          {review.firstName}
                        </span>
                        <span className="text-gray-400">{review.city}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getStatusColor(review.status)}`}>
                          {review.status}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                        <span>{formatDate(review.createdAt)}</span>
                        {review.projectType && (
                          <span className="text-orange-400">{review.projectType}</span>
                        )}
                        <div className="flex items-center gap-1">
                          <StarDisplay rating={review.overallRating} />
                          <span className="text-white ml-1">{review.overallRating}</span>
                        </div>
                      </div>
                      
                      {/* Email (hidden by default) */}
                      <div className="flex items-center gap-2 mb-3">
                        <button
                          onClick={() => toggleEmail(review.id)}
                          className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
                        >
                          {showEmails[review.id] ? (
                            <>
                              <EyeOff className="h-4 w-4" />
                              Hide email
                            </>
                          ) : (
                            <>
                              <Eye className="h-4 w-4" />
                              Show email
                            </>
                          )}
                        </button>
                        {showEmails[review.id] && (
                          <span className="text-white">{review.email}</span>
                        )}
                      </div>
                      
                      {/* Comment */}
                      <p className="text-gray-300 mb-3">{review.comment}</p>
                      
                      {/* Additional Info */}
                      <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                        <span>
                          Empfehlung: {review.recommendation ? "✅ Ja" : "❌ Nein"}
                        </span>
                        <span>
                          Foto-Erlaubnis: {getPhotoPermissionLabel(review.photoPermission)}
                        </span>
                      </div>
                      
                      {/* Detailed Ratings */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4 text-sm">
                        <span className="text-gray-400">Sauberkeit: <span className="text-white">{review.cleanlinessRating}/5</span></span>
                        <span className="text-gray-400">Ordnung: <span className="text-white">{review.worksiteOrderRating}/5</span></span>
                        <span className="text-gray-400">Qualität: <span className="text-white">{review.qualityRating}/5</span></span>
                        <span className="text-gray-400">Preis-Leistung: <span className="text-white">{review.valueRating}/5</span></span>
                        <span className="text-gray-400">Transparenz: <span className="text-white">{review.transparencyRating}/5</span></span>
                        <span className="text-gray-400">Kommunikation: <span className="text-white">{review.communicationRating}/5</span></span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex lg:flex-col gap-2">
                      {review.status === "pending" && (
                        <>
                          <Button
                            onClick={() => handleApprove(review.id)}
                            className="bg-green-600 hover:bg-green-700 flex-1"
                            data-testid={`approve-${review.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleReject(review.id)}
                            className="bg-red-600 hover:bg-red-700 flex-1"
                            data-testid={`reject-${review.id}`}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      <Button
                        onClick={() => handleDelete(review.id)}
                        variant="outline"
                        className="border-red-500 text-red-400 hover:bg-red-500/20 flex-1"
                        data-testid={`delete-${review.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
